import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { KeyRound, ShieldCheck, Mail, Bell, Download, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, loading, setUser, logout } = useAuth();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const confirmWord = lang === "en" ? "DELETE" : "SUPPRIMER";
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);

  // RGPD state
  const [exportBusy, setExportBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const exportData = async () => {
    setExportBusy(true);
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `sukanacoop-mes-donnees-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("rgpd.export_success"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setExportBusy(false);
    }
  };

  const performDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.post("/account/delete", { password: deletePassword, reason: deleteReason });
      toast.success(t("rgpd.delete_success"), { duration: 6000 });
      setDeleteOpen(false);
      // Wipe local session (both httpOnly cookie via /auth/logout and any legacy value)
      if (logout) await logout();
      setTimeout(() => { nav("/"); window.location.reload(); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setDeleteBusy(false);
    }
  };
  const [newsletterBusy, setNewsletterBusy] = useState(false);

  if (loading) return null;
  if (!user) return <Navigate to="/connexion"/>;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("Password must be at least 8 chars / Mot de passe min. 8 caractères"); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwords do not match / Ne correspondent pas"); return; }
    if (pw.current === pw.next) { toast.error("New password must differ / Doit être différent"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(t("profile.pw_changed"));
      setPw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  const toggleNewsletter = async () => {
    const nextValue = !user.newsletter_opt_in;
    setNewsletterBusy(true);
    try {
      await api.patch("/users/me/newsletter", { opt_in: nextValue });
      setUser({ ...user, newsletter_opt_in: nextValue });
      toast.success(nextValue
        ? (t("profile.newsletter_on") || "Vous recevrez les nouveautés SUKANACOOP ✨")
        : (t("profile.newsletter_off") || "Vous ne recevrez plus les nouveautés"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setNewsletterBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-6" data-testid="profile-page">
      <h1 className="heading font-extrabold text-4xl mb-8">{t("profile.title")}</h1>

      <div className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-3 mb-6">
        <div><p className="text-xs uppercase tracking-wider text-[#4B5563]">{t("profile.name")}</p><p className="font-semibold">{user.full_name}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-[#4B5563]">{t("profile.email")}</p><p className="font-semibold" data-testid="profile-email">{user.email}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-[#4B5563]">Role</p><p className="font-semibold">{user.role}</p></div>
        <div><p className="text-xs uppercase tracking-wider text-[#4B5563]">{t("profile.city")} · {t("profile.country")}</p><p className="font-semibold">{user.city}, {user.country}</p></div>
      </div>

      {/* Newsletter preferences (RGPD) */}
      <div className={`border rounded-lg p-6 mb-8 ${user.newsletter_opt_in ? "bg-green-50 border-green-200" : "bg-white border-[#1A3322]/10"}`} data-testid="newsletter-section">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${user.newsletter_opt_in ? "bg-green-100" : "bg-[#F5F5F0]"}`}>
            {user.newsletter_opt_in ? <Bell className="w-5 h-5 text-green-700"/> : <Mail className="w-5 h-5 text-[#1A3322]"/>}
          </div>
          <div className="flex-1">
            <h2 className="heading font-bold text-lg mb-1">
              {t("profile.newsletter_title") || "Communications SUKANACOOP"}
            </h2>
            <p className="text-sm text-[#4B5563]">
              {user.newsletter_opt_in
                ? (t("profile.newsletter_active") || "Vous recevez nos nouveautés, arrivages et promotions par email.")
                : (t("profile.newsletter_inactive") || "Recevez en avant-première nos nouveaux produits, arrivages et offres exclusives.")}
            </p>
            <Button
              onClick={toggleNewsletter}
              disabled={newsletterBusy}
              className={`mt-4 pill-btn rounded-full h-10 px-5 text-sm font-bold ${user.newsletter_opt_in ? "bg-white border border-[#1A3322]/20 text-[#1A3322] hover:bg-[#F5F5F0]" : "bg-[#C85A32] hover:bg-[#D98A2C] text-white"}`}
              data-testid="newsletter-toggle"
            >
              {newsletterBusy ? "…" : (user.newsletter_opt_in
                ? (t("profile.newsletter_unsubscribe") || "Se désabonner")
                : (t("profile.newsletter_subscribe") || "M'abonner aux nouveautés"))}
            </Button>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-[#1A3322]/10 rounded-lg p-6 space-y-4" data-testid="change-password-form">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#F5F5F0] flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-[#1A3322]"/>
          </div>
          <div>
            <h2 className="heading font-bold text-lg">{t("profile.password_section")}</h2>
          </div>
        </div>
        <div className="space-y-2">
          <Label>{t("profile.old_password")}</Label>
          <Input type="password" required value={pw.current} onChange={(e) => setPw({...pw, current: e.target.value})} data-testid="current-password"/>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{t("profile.new_password")}</Label>
            <Input type="password" minLength={8} required value={pw.next} onChange={(e) => setPw({...pw, next: e.target.value})} data-testid="new-password"/>
          </div>
          <div className="space-y-2">
            <Label>{t("profile.confirm_password")}</Label>
            <Input type="password" minLength={8} required value={pw.confirm} onChange={(e) => setPw({...pw, confirm: e.target.value})} data-testid="confirm-password"/>
          </div>
        </div>
        <Button type="submit" disabled={submitting} className="pill-btn bg-[#1A3322] hover:bg-[#24472F] rounded-full h-11 px-6" data-testid="change-password-submit">
          <ShieldCheck className="w-4 h-4 mr-2"/>{submitting ? "…" : t("profile.change_password")}
        </Button>
      </form>

      {/* ================ RGPD (Data export + Account deletion) ================ */}
      <div className="mt-12 mb-4">
        <h2 className="heading font-bold text-xl text-[#111812]">{t("rgpd.section_title")}</h2>
        <p className="text-xs text-[#4B5563] mt-1">{t("rgpd.section_desc")}</p>
      </div>

      <div className="bg-white border border-[#1A3322]/10 rounded-lg p-6 mb-4" data-testid="rgpd-export-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <h3 className="heading font-semibold text-base text-[#111812]">{t("rgpd.export_title")}</h3>
            <p className="text-xs text-[#4B5563] mt-1">{t("rgpd.export_desc")}</p>
          </div>
          <Button
            onClick={exportData}
            disabled={exportBusy}
            variant="outline"
            className="rounded-full border-[#1A3322]/30 text-[#1A3322] hover:bg-[#F5F5F0]"
            data-testid="rgpd-export-btn"
          >
            <Download className="w-4 h-4 mr-2"/>{exportBusy ? t("common.loading") : t("rgpd.export_cta")}
          </Button>
        </div>
      </div>

      <div className="bg-[#FEF2F2] border border-red-200 rounded-lg p-6" data-testid="rgpd-delete-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <h3 className="heading font-semibold text-base text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4"/>{t("rgpd.delete_title")}
            </h3>
            <p className="text-xs text-red-900/80 mt-1">{t("rgpd.delete_desc")}</p>
          </div>
          <Button
            onClick={() => { setDeleteOpen(true); setDeleteConfirmText(""); setDeletePassword(""); setDeleteReason(""); }}
            variant="destructive"
            className="rounded-full"
            data-testid="rgpd-delete-btn"
          >
            <Trash2 className="w-4 h-4 mr-2"/>{t("rgpd.delete_cta")}
          </Button>
        </div>
      </div>

      {/* Confirmation modal */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="rgpd-delete-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5"/>{t("rgpd.modal_title")}
            </DialogTitle>
            <DialogDescription>{t("rgpd.modal_desc")}</DialogDescription>
          </DialogHeader>

          <ul className="text-xs text-[#4B5563] list-disc pl-5 space-y-1 my-2 bg-[#F5F5F0] p-3 rounded">
            <li>{t("rgpd.modal_kept")}</li>
            <li>{t("rgpd.modal_erased")}</li>
            <li>{t("rgpd.modal_login")}</li>
          </ul>

          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("rgpd.modal_confirm_label")}</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={confirmWord}
                data-testid="rgpd-confirm-input"
              />
            </div>
            <div className="space-y-1">
              <Label>{t("rgpd.modal_password_label")}</Label>
              <Input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                data-testid="rgpd-password-input"
              />
            </div>
            <div className="space-y-1">
              <Label>{t("rgpd.modal_reason_label")}</Label>
              <Textarea
                rows={2}
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder={t("rgpd.modal_reason_placeholder")}
                data-testid="rgpd-reason-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteOpen(false)} data-testid="rgpd-cancel">
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteBusy || deleteConfirmText !== confirmWord || !deletePassword}
              onClick={performDelete}
              data-testid="rgpd-confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2"/>{deleteBusy ? "…" : t("rgpd.modal_confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
